import React from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import type { CacheMetadata } from '@/types/reporting-v2';

interface CacheIndicatorProps {
  metadata: CacheMetadata;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export default function CacheIndicator({
  metadata,
  onRefresh,
  refreshing = false,
}: CacheIndicatorProps) {
  const formatTime = (date: string) => {
    const now = new Date();
    const cached = new Date(date);
    const diffMs = now.getTime() - cached.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getStatusColor = () => {
    if (!metadata.is_fresh) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getStatusColor()}`}>
      <Clock className="w-4 h-4" />
      <span className="text-xs font-medium">
        Cached {formatTime(metadata.cached_at)}
        {metadata.ttl_seconds && ` • Expires in ${Math.floor(metadata.ttl_seconds / 60)}m`}
      </span>
      {onRefresh && (
        <>
          <div className="w-px h-4 bg-current opacity-30" />
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="hover:opacity-70 transition-opacity disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </>
      )}
    </div>
  );
}
